import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { claimsAPI, documentsAPI, messagesAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, FileText, MessageSquare, Upload, Clock, Download, Eye } from 'lucide-react';
import { CLAIM_STATUS, USER_ROLE, formatDate, formatDateTime, formatCurrency, getStatusColor, getPriorityColor } from '@/utils/constants';
import { toast } from 'sonner';
import DocumentPreviewModal from '@/components/DocumentPreviewModal';

const ClaimDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    priority: '',
  });
  const [previewDocument, setPreviewDocument] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    fetchClaimDetails();
  }, [id]);

  const fetchClaimDetails = async () => {
    try {
      const [claimRes, docsRes, msgsRes] = await Promise.all([
        claimsAPI.getById(id),
        documentsAPI.getByClaim(id),
        messagesAPI.getByClaim(id),
      ]);
      setClaim(claimRes.data);
      setDocuments(docsRes.data);
      setMessages(msgsRes.data);
      setStatusUpdate({
        status: claimRes.data.status,
        priority: claimRes.data.priority,
      });
    } catch (error) {
      toast.error('Failed to load claim details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      await documentsAPI.upload(id, file);
      toast.success('Document uploaded successfully');
      const docsRes = await documentsAPI.getByClaim(id);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      await messagesAPI.create({ claim_id: id, message: newMessage });
      setNewMessage('');
      const msgsRes = await messagesAPI.getByClaim(id);
      setMessages(msgsRes.data);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStatusUpdate = async () => {
    setUpdatingStatus(true);
    try {
      const updateData = {};
      if (statusUpdate.status !== claim.status) updateData.status = statusUpdate.status;
      if (statusUpdate.priority !== claim.priority) updateData.priority = statusUpdate.priority;
      
      if (Object.keys(updateData).length > 0) {
        await claimsAPI.update(id, updateData);
        toast.success('Claim updated successfully');
        fetchClaimDetails();
      }
    } catch (error) {
      toast.error('Failed to update claim');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading claim details...</div>;
  }

  if (!claim) {
    return <div className="text-center py-12">Claim not found</div>;
  }

  const canUpdateStatus = user?.role === USER_ROLE.BROKER || user?.role === USER_ROLE.ADMIN;
  const canDownload = user?.role === USER_ROLE.BROKER || user?.role === USER_ROLE.ADMIN;

  const handleDownloadDocument = (documentId, fileName) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/documents/download/${documentId}`;
    
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded successfully');
    })
    .catch(error => {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    });
  };

  const handlePreviewDocument = (document) => {
    setPreviewDocument(document);
    setIsPreviewOpen(true);
  };

  return (
    <div className="space-y-6">
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Claim Details
          </h1>
          <div className="flex items-center gap-3">
            <span 
              className="text-lg font-mono font-semibold text-slate-700"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {claim.claim_id}
            </span>
            <Badge className={getStatusColor(claim.status)}>{claim.status}</Badge>
            <Badge className={getPriorityColor(claim.priority)}>{claim.priority}</Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Claim Information */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Claim Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Policy Number</div>
                  <div className="font-medium">{claim.policy_number}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Insured Name</div>
                  <div className="font-medium">{claim.insured_name}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Claim Type</div>
                  <div className="font-medium">{claim.claim_type}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Estimated Loss</div>
                  <div className="font-medium">{formatCurrency(claim.estimated_loss)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Incident Date</div>
                  <div className="font-medium">{claim.incident_date} {claim.incident_time}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Submitted On</div>
                  <div className="font-medium">{formatDate(claim.created_at)}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-1">Incident Location</div>
                <div className="font-medium">{claim.incident_location}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-1">Description</div>
                <div className="text-slate-700 whitespace-pre-wrap">{claim.description}</div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for Documents and Messages */}
          <Tabs defaultValue="timeline" className="w-full">
            <TabsList>
              <TabsTrigger value="timeline" data-testid="timeline-tab">Timeline</TabsTrigger>
              <TabsTrigger value="documents" data-testid="documents-tab">Documents ({documents.length})</TabsTrigger>
              <TabsTrigger value="messages" data-testid="messages-tab">Messages ({messages.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Status Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {claim.status_history && claim.status_history.length > 0 ? (
                      claim.status_history.map((entry, index) => (
                        <div key={index} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="h-3 w-3 rounded-full bg-blue-600"></div>
                            {index < claim.status_history.length - 1 && (
                              <div className="flex-1 w-0.5 bg-slate-200 min-h-[40px]"></div>
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={getStatusColor(entry.status)}>{entry.status}</Badge>
                              <span className="text-xs text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                                {formatDateTime(entry.timestamp)}
                              </span>
                            </div>
                            {entry.note && (
                              <p className="text-sm text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>{entry.note}</p>
                            )}
                            {entry.updated_by && (
                              <p className="text-xs text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>By: {entry.updated_by}</p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        No timeline history available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="documents">
              <Card className="border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Documents</CardTitle>
                    <div>
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                      />
                      <Button
                        data-testid="upload-document-button"
                        size="sm"
                        onClick={() => document.getElementById('file-upload').click()}
                        disabled={uploadingFile}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingFile ? 'Uploading...' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                          <FileText className="h-5 w-5 text-slate-500" />
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ fontFamily: 'Public Sans, sans-serif' }}>{doc.file_name}</div>
                            <div className="text-xs text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                              Uploaded {formatDateTime(doc.uploaded_at)}
                            </div>
                          </div>
                          {canDownload && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePreviewDocument(doc)}
                                data-testid={`preview-doc-${doc.id}`}
                                className="gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                Preview
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                                data-testid={`download-doc-${doc.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                      No documents uploaded yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="messages">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
                    {messages.length > 0 ? (
                      messages.map((msg) => (
                        <div key={msg.id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                              {msg.sender_name}
                            </span>
                            <span className="text-xs text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                              {formatDateTime(msg.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700" style={{ fontFamily: 'Public Sans, sans-serif' }}>{msg.message}</p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                        No messages yet
                      </div>
                    )}
                  </div>
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      data-testid="message-input"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={sendingMessage}
                    />
                    <Button type="submit" data-testid="send-message-button" disabled={sendingMessage}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Send
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {canUpdateStatus && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Update Claim</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="status" style={{ fontFamily: 'Public Sans, sans-serif' }}>Status</Label>
                  <Select
                    value={statusUpdate.status}
                    onValueChange={(value) => setStatusUpdate({ ...statusUpdate, status: value })}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(CLAIM_STATUS).map((status) => (
                        <SelectItem key={status} value={status} data-testid={`status-option-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority" style={{ fontFamily: 'Public Sans, sans-serif' }}>Priority</Label>
                  <Select
                    value={statusUpdate.priority}
                    onValueChange={(value) => setStatusUpdate({ ...statusUpdate, priority: value })}
                  >
                    <SelectTrigger data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low" data-testid="priority-option-low">Low</SelectItem>
                      <SelectItem value="normal" data-testid="priority-option-normal">Normal</SelectItem>
                      <SelectItem value="high" data-testid="priority-option-high">High</SelectItem>
                      <SelectItem value="urgent" data-testid="priority-option-urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  data-testid="update-claim-button"
                  onClick={handleStatusUpdate} 
                  disabled={updatingStatus}
                  className="w-full"
                >
                  {updatingStatus ? 'Updating...' : 'Update Claim'}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Documents</span>
                <span className="font-semibold">{documents.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Messages</span>
                <span className="font-semibold">{messages.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Last Updated</span>
                <span className="font-semibold text-xs">{formatDate(claim.updated_at)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        document={previewDocument}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onDownload={handleDownloadDocument}
      />
    </div>
  );
};

export default ClaimDetailPage;
